/**
 * ActivitiesDualPage — Sales Manager dual-tab Activities view.
 *
 * Ported from v2 Claude Design `aura-exec.jsx` → `PageActivitiesDual`.
 *   Personal tab: my own day's schedule + week stats
 *     GET /api/v1/tasks?userId=me&window=today+week
 *     GET /api/v1/role-kpis/sales/personal/activities?userId=me
 *   Team tab: aggregated team calls/meetings/visits
 *     GET /api/v1/tasks?team=sales&window=today+week
 *     GET /api/v1/role-kpis/sales/team/activities
 *
 * Tab state persists in URL query param `?tab=personal|team`.
 * UI-only · no backend changes · tenant-agnostic (D-09).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageFrame,
  Tabs,
  Kpi,
  DataTable,
  Progress,
  Avatar,
  LoadingBlock,
  ErrorBlock,
  EmptyBlock,
  useFetch,
  compactINR,
  type DataTableRow,
} from '@/components/executive/ExecAtoms';

interface Task {
  id: string;
  title: string;
  scheduledAt?: string | null;
  type?: string;
  status?: string;
  completed?: boolean;
}

interface PersonalStats {
  calls?: number;
  meetings?: number;
  visits?: number;
  completionPct?: number;
}

interface TeamAggRow {
  userId?: string;
  name: string;
  calls?: number;
  meetings?: number;
  visits?: number;
  closedValue?: number;
  utilizationPct?: number;
}

interface TeamStats {
  calls?: { today?: number; deltaPct?: number };
  meetings?: { today?: number; deltaAbs?: number };
  visits?: { today?: number };
  closedValue?: { today?: number };
  rows?: TeamAggRow[];
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const TABS = ['Team', 'Personal'];

export function ActivitiesDualPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab')?.toLowerCase();
  const tab = rawTab === 'personal' ? 'Personal' : 'Team';

  const setTab = useCallback(
    (t: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', t.toLowerCase());
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Personal data
  const personalTasks = useFetch<Task[] | { items: Task[] }>(
    tab === 'Personal' ? '/tasks?userId=me&window=today+week' : null,
  );
  const personalStatsState = useFetch<PersonalStats>(
    tab === 'Personal' ? '/role-kpis/sales/personal/activities?userId=me' : null,
  );

  // Team data
  const teamTasks = useFetch<Task[] | { items: Task[] }>(
    tab === 'Team' ? '/tasks?team=sales&window=today+week' : null,
  );
  const teamStatsState = useFetch<TeamStats>(
    tab === 'Team' ? '/role-kpis/sales/team/activities' : null,
  );

  const personal = useMemo<Task[]>(() => {
    if (!personalTasks.data) return [];
    return Array.isArray(personalTasks.data)
      ? personalTasks.data
      : Array.isArray((personalTasks.data as { items?: Task[] }).items)
        ? (personalTasks.data as { items: Task[] }).items
        : [];
  }, [personalTasks.data]);

  const teamAgg = teamStatsState.data?.rows ?? [];

  const loading =
    tab === 'Personal'
      ? personalTasks.loading || personalStatsState.loading
      : teamStatsState.loading || teamTasks.loading;
  const err =
    tab === 'Personal'
      ? personalTasks.error || personalStatsState.error
      : teamStatsState.error || teamTasks.error;

  return (
    <PageFrame
      title="Activities"
      sub="Personal schedule · team activity aggregation"
    >
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {loading && <LoadingBlock />}
      {err && err !== 'not-available' && (
        <ErrorBlock message="Could not load activities." />
      )}
      {err === 'not-available' && (
        <EmptyBlock message="Activities endpoint not available yet." />
      )}

      {!loading && !err && tab === 'Personal' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 320px',
            gap: 16,
          }}
        >
          <div className="card" style={{ padding: 6 }}>
            {personal.length === 0 ? (
              <div
                style={{
                  padding: 40,
                  textAlign: 'center',
                  color: 'var(--ink-3)',
                  fontSize: 12,
                }}
              >
                No tasks scheduled.
              </div>
            ) : (
              personal.map((a, i) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex',
                    gap: 14,
                    padding: 14,
                    borderBottom:
                      i === personal.length - 1
                        ? 'none'
                        : '1px solid var(--line)',
                    opacity: a.completed ? 0.5 : 1,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      width: 50,
                      color: 'var(--ink-3)',
                      fontFamily: 'var(--ff-mono)',
                    }}
                  >
                    {fmtTime(a.scheduledAt)}
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked={a.completed}
                    style={{ accentColor: 'var(--ink-0)' }}
                    readOnly
                  />
                  <div
                    style={{
                      flex: 1,
                      fontSize: 13,
                      textDecoration: a.completed ? 'line-through' : 'none',
                      color: 'var(--ink-0)',
                    }}
                  >
                    {a.title}
                  </div>
                  {a.type && (
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.06,
                      }}
                    >
                      {a.type}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                marginBottom: 8,
              }}
            >
              My week
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
                fontSize: 12,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontFamily: 'var(--ff-serif)',
                    color: 'var(--ink-0)',
                  }}
                >
                  {personalStatsState.data?.calls ?? '—'}
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Calls</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontFamily: 'var(--ff-serif)',
                    color: 'var(--ink-0)',
                  }}
                >
                  {personalStatsState.data?.meetings ?? '—'}
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                  Meetings
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontFamily: 'var(--ff-serif)',
                    color: 'var(--ink-0)',
                  }}
                >
                  {personalStatsState.data?.visits ?? '—'}
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                  Visits
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontFamily: 'var(--ff-serif)',
                    color: 'var(--ink-0)',
                  }}
                >
                  {personalStatsState.data?.completionPct != null
                    ? `${personalStatsState.data.completionPct}%`
                    : '—'}
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                  Completion
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !err && tab === 'Team' && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
              marginBottom: 14,
            }}
          >
            <Kpi
              label="Team calls today"
              value={String(teamStatsState.data?.calls?.today ?? '—')}
              delta={
                teamStatsState.data?.calls?.deltaPct != null
                  ? `${teamStatsState.data.calls.deltaPct > 0 ? '+' : ''}${teamStatsState.data.calls.deltaPct}% vs last Tue`
                  : undefined
              }
              color="var(--success)"
            />
            <Kpi
              label="Meetings"
              value={String(teamStatsState.data?.meetings?.today ?? '—')}
              delta={
                teamStatsState.data?.meetings?.deltaAbs != null
                  ? `${teamStatsState.data.meetings.deltaAbs > 0 ? '+' : ''}${teamStatsState.data.meetings.deltaAbs}`
                  : undefined
              }
            />
            <Kpi
              label="Site visits"
              value={String(teamStatsState.data?.visits?.today ?? '—')}
            />
            <Kpi
              label="Team closed today"
              value={
                typeof teamStatsState.data?.closedValue?.today === 'number'
                  ? compactINR(teamStatsState.data.closedValue.today)
                  : '—'
              }
              color="var(--success)"
            />
          </div>

          {teamAgg.length === 0 ? (
            <EmptyBlock message="No team activity recorded yet." />
          ) : (
            <DataTable
              columns={[
                { k: 'n', label: 'Rep', w: '220px' },
                {
                  k: 'calls',
                  label: 'Calls',
                  w: '80px',
                  align: 'right',
                  mono: true,
                },
                {
                  k: 'meets',
                  label: 'Meetings',
                  w: '100px',
                  align: 'right',
                  mono: true,
                  muted: true,
                },
                {
                  k: 'visits',
                  label: 'Visits',
                  w: '90px',
                  align: 'right',
                  mono: true,
                  muted: true,
                },
                {
                  k: 'closed',
                  label: 'Closed',
                  w: '110px',
                  align: 'right',
                  mono: true,
                },
                { k: 'util', label: 'Utilization', w: '200px' },
              ]}
              rows={teamAgg.map((r): DataTableRow => ({
                n: () => (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <Avatar name={r.name} size={24} />
                    <span>{r.name}</span>
                  </div>
                ),
                calls: r.calls ?? 0,
                meets: r.meetings ?? 0,
                visits: r.visits ?? 0,
                closed:
                  typeof r.closedValue === 'number'
                    ? compactINR(r.closedValue)
                    : '—',
                util: () => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Progress
                      value={r.utilizationPct ?? 0}
                      color={
                        (r.utilizationPct ?? 0) >= 80
                          ? 'var(--success)'
                          : (r.utilizationPct ?? 0) >= 60
                            ? 'var(--saffron-600)'
                            : 'var(--danger)'
                      }
                      h={4}
                    />
                    <span className="num" style={{ fontSize: 11 }}>
                      {r.utilizationPct ?? 0}%
                    </span>
                  </div>
                ),
              }))}
            />
          )}
        </>
      )}
    </PageFrame>
  );
}

export default ActivitiesDualPage;
